import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CustomSelect from '../components/CustomSelect';
import './AccountWithdraw.css';
import { logout, checkAuth, handleAuthError } from '../utils/auth';
import { API_URL } from '../config/data';

const AccountWithdraw = () => {
  // Состояния для данных
  const [user, setUser] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [tradingAccounts, setTradingAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Состояния для формы
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedTargetAccount, setSelectedTargetAccount] = useState('');
  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('Вывод средств');
  
  // Состояния для ошибок
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Состояние для модального окна успеха
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  // Функция для получения токена из localStorage
  const getAuthToken = () => {
    return localStorage.getItem('authToken');
  };

  // Функция для получения профиля пользователя
  const fetchUserProfile = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Токен авторизации не найден');
      }

      const response = await axios.get(`${API_URL}/api/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data.user;
    } catch (error) {
      console.error('Ошибка при получении профиля:', error);
      throw error;
    }
  };

  // Функция для получения банковских счетов пользователя
  const fetchUserAccounts = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Токен авторизации не найден');
      }

      const response = await axios.get(`${API_URL}/api/accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data.accounts;
    } catch (error) {
      console.error('Ошибка при получении банковских счетов:', error);
      throw error;
    }
  };

  // Функция для получения торговых счетов пользователя
  const fetchTradingAccounts = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Токен авторизации не найден');
      }

      const response = await axios.get(`${API_URL}/api/trading-accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data.accounts;
    } catch (error) {
      console.error('Ошибка при получении торговых счетов:', error);
      throw error;
    }
  };

  // Функция для получения имени пользователя
  const getUserDisplayName = () => {
    if (!user) return 'Загрузка...';
    return `${user.lastName} ${user.firstName}`;
  };

  // Функция для получения должности
  const getUserPosition = () => {
    if (!user) return 'Загрузка...';
    return user.role || 'Квалифицированный управляющий';
  };

  // Функция для форматирования суммы
  const formatBalance = (amount) => {
    return new Intl.NumberFormat('ru-RU').format(amount);
  };

  // Обработчики для модального окна
  const handleSuccessPopupClose = (e) => {
    if (e.target === e.currentTarget) {
      setShowSuccessPopup(false);
    }
  };

  const handleSuccessButtonClick = () => {
    setShowSuccessPopup(false);
  };

  // Загрузка данных при монтировании компонента
  useEffect(() => {
    // Проверяем авторизацию перед загрузкой данных
    if (!checkAuth()) {
      return;
    }

    const loadData = async () => {
      try {
        setIsLoading(true);
        const [userData, accountsData, tradingAccountsData] = await Promise.all([
          fetchUserProfile(),
          fetchUserAccounts(),
          fetchTradingAccounts()
        ]);
        
        setUser(userData);
        setAccounts(accountsData);
        setTradingAccounts(tradingAccountsData);
      } catch (error) {
        console.error('Ошибка при загрузке данных:', error);
        
        // Обрабатываем ошибки авторизации
        if (handleAuthError(error)) {
          return;
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Обработчик для закрытия модального окна по Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showSuccessPopup) {
        setShowSuccessPopup(false);
      }
    };

    if (showSuccessPopup) {
      document.addEventListener('keydown', handleEscape);
      document.body.classList.add('popup-open');
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.classList.remove('popup-open');
      };
    }
  }, [showSuccessPopup]);

  // Функция для создания опций торговых счетов
  const getTradingAccountOptions = () => {
    return [
      { value: '', text: 'Счет' },
      ...tradingAccounts.map(account => ({
        value: account.id.toString(),
        text: `${account.account_number} ${account.currency} (${Number(account.balance) || 0} ${account.currency})`
      }))
    ];
  };

  // Функция для создания опций банковских счетов
  const getBankAccountOptions = () => {
    const bankAccounts = accounts.filter(account => {
      const hasBank = account.bank && account.bank.trim() !== '';
      const hasNumber = account.number && account.number.trim() !== '';
      return hasBank && hasNumber;
    });
    
    return [
      { value: '', text: 'Выберите счет зачисления' },
      { value: 'add', text: 'Добавить новый счет', isLink: true, href: '/account/add' },
      ...bankAccounts.map(account => ({
        value: account.id.toString(),
        text: `${account.bank} ${account.number}`
      }))
    ];
  };

  // Функция для создания заявки на вывод
  const createWithdrawal = async (e) => {
    e.preventDefault();
    
    // Валидация
    if (!selectedAccount) {
      setErrors({ account: 'Выберите счет для списания' });
      return;
    }
    
    if (!selectedTargetAccount) {
      setErrors({ targetAccount: 'Выберите счет зачисления' });
      return;
    }
    
    if (!amount || parseFloat(amount) <= 0) {
      setErrors({ amount: 'Введите корректную сумму' });
      return;
    }
    
    // Дополнительная проверка баланса на клиенте
    const sourceAccount = tradingAccounts.find(acc => acc.id.toString() === selectedAccount);
    if (sourceAccount) {
      const totalBalance = Number(sourceAccount.balance) || 0;
      const requestedAmount = parseFloat(amount);
      
      if (requestedAmount > totalBalance) {
        setErrors({ 
          amount: `Недостаточно средств. Текущий баланс: ${formatBalance(totalBalance)} ${sourceAccount.currency}, запрашиваемая сумма: ${formatBalance(requestedAmount)} ${sourceAccount.currency}` 
        });
        return;
      }
    }

    try {
      setIsSubmitting(true);
      setErrors({});
      
      const token = getAuthToken();
      if (!token) {
        throw new Error('Токен авторизации не найден');
      }

      // Находим выбранные счета (sourceAccount уже найден выше)
      const targetAccount = accounts.find(acc => acc.id.toString() === selectedTargetAccount);

      if (!sourceAccount || !targetAccount) {
        throw new Error('Выбранные счета не найдены');
      }

      // Подготавливаем данные для отправки
      const totalBalance = Number(sourceAccount.balance) || 0;
      const depositAmount = Number(sourceAccount.deposit_amount) || 0;
      const profitAmount = Number(sourceAccount.profit) || 0;
      
      console.log('Отладочная информация:', {
        sourceAccount,
        totalBalance,
        depositAmount,
        profitAmount,
        requestedAmount: parseFloat(amount),
        currency: sourceAccount.currency
      });
      
      const withdrawalData = {
        amount: parseFloat(amount),
        recipientDetails: {
          bankName: targetAccount.bank,
          number: targetAccount.number,
          accountNumber: sourceAccount.account_number
        },
        comment: comment,
        // Добавляем информацию о полном балансе для проверки на сервере
        totalBalance: totalBalance,
        depositAmount: depositAmount,
        profitAmount: profitAmount,
        currency: sourceAccount.currency
      };

      const response = await axios.post(`${API_URL}/api/operations/withdrawal`, withdrawalData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.message === 'Withdrawal request created successfully') {
        // Сбрасываем форму
        setSelectedAccount('');
        setSelectedTargetAccount('');
        setAmount('');
        setComment('Вывод средств');
        // Очищаем ошибки
        setErrors({});
        // Показываем модальное окно успеха
        setShowSuccessPopup(true);
      }
    } catch (error) {
      console.error('Ошибка при создании заявки на вывод:', error);
      
      // Обрабатываем ошибки авторизации
      if (handleAuthError(error)) {
        return;
      }
      
      if (error.response?.data?.message === 'Insufficient funds') {
        // Обрабатываем ошибку недостаточности средств
        const { currentBalance, requestedAmount } = error.response.data;
        const sourceAccount = tradingAccounts.find(acc => acc.id.toString() === selectedAccount);
        const totalBalance = sourceAccount ? Number(sourceAccount.balance) || 0 : currentBalance;
        setErrors({ 
          amount: `Недостаточно средств. Текущий баланс: ${formatBalance(totalBalance)} ${sourceAccount?.currency || '₽'}, запрашиваемая сумма: ${formatBalance(requestedAmount)} ${sourceAccount?.currency || '₽'}` 
        });
      } else if (error.response?.data?.message === 'Account not found or inactive') {
        // Обрабатываем ошибку не найденного счета
        setErrors({ 
          account: 'Выбранный счет не найден или неактивен. Пожалуйста, выберите другой счет.' 
        });
      } else if (error.response?.data?.message === 'Account number is required for withdrawal') {
        // Обрабатываем ошибку отсутствия номера счета
        setErrors({ 
          general: 'Ошибка: не указан номер счета для списания.' 
        });
      } else if (error.response?.data?.errors) {
        // Обрабатываем ошибки валидации с сервера
        const validationErrors = {};
        error.response.data.errors.forEach(err => {
          validationErrors[err.path] = err.msg;
        });
        setErrors(validationErrors);
      } else {
        setErrors({ general: 'Ошибка при создании заявки на вывод. Попробуйте еще раз.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Показываем загрузку пока данные не загружены
  if (isLoading) {
    return (
      <div className="wrapper">
        <div className="loading">
          <p>Загрузка данных...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wrapper">
      <header data-fls-header-accaunt="" className="header-accaunt">
        <div className="header-accaunt__container">
          <div className="header-accaunt__body">
            <a href="/account" className="header-accaunt__logo">
              <img src="/assets/img/logo.svg" alt="Logo" />
            </a>
            <div className="header-accaunt__actions">
              <div className="header-accaunt__settings">
                <a href="/account/setting" className="--icon-settings"></a>
              </div>
              <div className="header-accaunt__profile">
                <a href="" className="--icon-profile">
                  <div className="header-accaunt__content-profile">
                    <span>{getUserDisplayName()}</span>
                    <span>{getUserPosition()}</span>
                  </div>
                </a>
              </div>
              <button 
                data-fls-button="" 
                type="button" 
                className="header-accaunt__logout header-accaunt__logout--icon-log-out"
                onClick={logout}
              ></button>
            </div>
          </div>
        </div>
      </header>
      <main className="page account">
        <div className="account__container">
          <div className="account__body">
            <nav data-fls-block="" data-fls-aside="" className="aside">
              <ul className="aside__list">
                <li className="aside__item">
                  <a href="/account" className="aside__link aside__link--icon-home">Главная</a>
                </li>
                <li className="aside__item">
                  <a href="/account/expense" className="aside__link aside__link--icon-wallet">Счета</a>
                </li>
                <li className="aside__item">
                  <a href="/account/history" className="aside__link aside__link--icon-history">История</a>
                </li>
                <li className="aside__item">
                  <a href="/account/withdraw" className="aside__link aside__link--active aside__link--icon-withdraw">Вывести</a>
                </li>
              </ul>
              <div className="aside__actions">
                <a href="/account/withdraw" className="aside__action aside__action--icon-withdraw">Вывести деньги</a>
                <a href="/account/check" className="aside__action aside__action--icon-deposit">Пополнить баланс</a>
              </div>
            </nav>
            <div className="account__content">
              <h3 className="account__title account__title--title-25">Вывод денег</h3>
              <form onSubmit={createWithdrawal} data-fls-form="" data-fls-maxwidth="" className="account__withdraw withdraw">
                {/* Сообщения об ошибках */}
                {errors.general && (
                  <div className="withdraw__error" style={{ color: 'red', marginBottom: '1rem' }}>
                    {errors.general}
                  </div>
                )}

                <div data-fls-block="" className="withdraw__block">
                  <h4 className="withdraw__subtitle withdraw__subtitle--title-20">Счет и сумма списания</h4>
                  <div className="withdraw__label">
                    <CustomSelect
                      options={getTradingAccountOptions()}
                      value={selectedAccount}
                      onChange={setSelectedAccount}
                      modif="base"
                      placeholder="Счет"
                    />
                    {errors.account && (
                      <div className="withdraw__field-error">{errors.account}</div>
                    )}
                    <input 
                      data-fls-input="" 
                      required 
                      autoComplete="off" 
                      type="number" 
                      name="amount" 
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      data-error="Error" 
                      placeholder="Сумма" 
                      className={`input ${errors.amount ? 'input--error' : ''}`}
                      step="0.01"
                      min="0"
                    />
                    {errors.amount && (
                      <div className="withdraw__field-error">{errors.amount}</div>
                    )}
                  </div>
                </div>
                <div data-fls-block="" className="withdraw__block">
                  <h4 className="withdraw__subtitle withdraw__subtitle--title-20">Счет зачисления</h4>
                  <CustomSelect
                    options={getBankAccountOptions()}
                    value={selectedTargetAccount}
                    onChange={setSelectedTargetAccount}
                    modif="base"
                    placeholder="Выберите счет зачисления"
                  />
                  {errors.targetAccount && (
                    <div className="withdraw__field-error">{errors.targetAccount}</div>
                  )}
                </div>
                <button 
                  type="submit" 
                  data-fls-button="" 
                  className="withdraw__button button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Создание заявки...' : 'Вывести деньги'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </main>

      {/* Модальное окно успеха */}
      {showSuccessPopup && (
        <div 
          data-fls-popup="success" 
          aria-hidden="false" 
          className={`popup ${showSuccessPopup ? 'show' : ''}`} 
          onClick={handleSuccessPopupClose}
        >
          <div data-fls-popup-wrapper="" className="popup__wrapper">
            <div data-fls-popup-body="" className="popup__body success">
              <h3 className="success__title success__title--title-20">Успешно!</h3>
              <div className="success__content">
                <p className="success__message">
                  Заявка на вывод средств создана успешно! В ближайшее время средства будут зачислены на ваш банковский счет.
                </p>
              </div>
              <div className="success__actions">
                <button 
                  data-fls-button="" 
                  type="button" 
                  className="success__button button"
                  onClick={handleSuccessButtonClick}
                >
                  Понятно
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountWithdraw;